/**
 * DistribuicoesPendentesWidget — S1 Bloco A
 * Espelho do Comercial: cards sem distribuição em item que exige vínculo +
 * publicados aguardando confirmação de crédito. (INFOs devidas chegam no S2.)
 * Renderizar apenas para papel Comercial ou Coordenação.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Share2, ChevronRight, Clock3, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { pendentesDeDistribuicao, aguardandoConfirmacaoCredito, parseCreditos } from '@/lib/distribuicao';
import type { Demanda } from '@/lib/types';

export function DistribuicoesPendentesWidget({ demandas: demandasProp }: { demandas?: Demanda[] }) {
  const navigate = useNavigate();
  const demandasQ = useQuery({
    queryKey: ['demandas'],
    queryFn: () => api.demandas.list().then((r) => r.data),
    staleTime: 30_000,
    enabled: !demandasProp,
  });
  const demandas = demandasProp ?? demandasQ.data ?? [];

  const semDistribuicao = useMemo(() => pendentesDeDistribuicao(demandas), [demandas]);
  const aguardandoCredito = useMemo(() => aguardandoConfirmacaoCredito(demandas), [demandas]);
  const total = semDistribuicao.length + aguardandoCredito.length;

  return (
    <div className="bg-surface border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Share2 className="w-4 h-4 text-muted" />
          <h2 className="text-sm font-semibold">Distribuições pendentes</h2>
          <span className="text-xs text-muted">{total}</span>
        </div>
        <button
          type="button"
          className="text-xs text-muted hover:text-fg flex items-center gap-0.5"
          onClick={() => navigate('/demandas')}
        >
          Ver demandas <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {total === 0 ? (
        <p className="text-xs text-muted py-2">Nenhuma pendência de distribuição. 👌</p>
      ) : (
        <div className="space-y-3">
          {semDistribuicao.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted mb-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 text-yellow" /> Sem contratos vinculados ({semDistribuicao.length})
              </div>
              <div className="space-y-1">
                {semDistribuicao.slice(0, 6).map((d) => (
                  <Linha key={d.id} d={d} onClick={() => navigate('/demandas')} detalhe={d.status} />
                ))}
              </div>
            </div>
          )}
          {aguardandoCredito.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted mb-1 flex items-center gap-1">
                <Clock3 className="w-3 h-3 text-yellow" /> Créditos aguardando confirmação ({aguardandoCredito.length})
              </div>
              <div className="space-y-1">
                {aguardandoCredito.slice(0, 6).map((d) => {
                  const n = parseCreditos(d.distribuicaoCreditos).filter((c) => c.status === 'aguardando').length;
                  return <Linha key={d.id} d={d} onClick={() => navigate('/demandas')} detalhe={`${n} contrato(s)`} />;
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Linha({ d, detalhe, onClick }: { d: Demanda; detalhe: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between gap-2 text-left text-xs px-2 py-1.5 rounded hover:bg-border/30"
    >
      <span className="truncate">{d.titulo}</span>
      <span className="text-muted shrink-0">{detalhe}</span>
    </button>
  );
}
